import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Clarity from '@microsoft/clarity';

// Custom hook for Microsoft Clarity tracking
export const useClarityTracking = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    // Only track if Clarity is initialized
    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        // Set custom page ID for better tracking
        Clarity.identify(undefined, undefined, location.pathname);
        console.log('Clarity page view tracked:', location.pathname);
      } catch (error) {
        console.warn('Failed to track page view with Clarity:', error);
      }
    }
  }, [location.pathname]);

  // Utility functions for custom event tracking
  const trackEvent = (eventName: string) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.event(eventName);
        console.log('Clarity event tracked:', eventName);
      } catch (error) {
        console.warn('Failed to track event with Clarity:', error);
      }
    }
  };

  const setTag = (key: string, value: string | string[]) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.setTag(key, value);
        console.log('Clarity tag set:', key, value);
      } catch (error) {
        console.warn('Failed to set tag with Clarity:', error);
      }
    }
  };

  const identifyUser = (customId: string, friendlyName?: string) => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.identify(customId, undefined, undefined, friendlyName);
        console.log('Clarity user identified:', customId, friendlyName);
      } catch (error) {
        console.warn('Failed to identify user with Clarity:', error);
      }
    }
  };

  return {
    trackEvent,
    setTag,
    identifyUser,
  };
};